/**
 * Project Manager Dashboard
 * Comprehensive dashboard for professional project managers
 * Features: Project oversight, contractor management, geofencing, documentation, analytics
 * Live data: project cards, site-visit history, documents/reports (Task 10)
 * Generate Report dialog: wired to createReport API (Task 11)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Tabs,
    Tab,
    Avatar,
    Chip,
    LinearProgress,
    IconButton,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    ListItemSecondaryAction,
    Divider,
    TextField,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    AlertTitle,
    Badge,
    Tooltip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Collapse,
    CircularProgress
} from '@mui/material';
import {
    Dashboard,
    Assignment,
    People,
    LocationOn,
    Description,
    TrendingUp,
    Notifications,
    Search,
    FilterList,
    Add,
    Edit,
    Visibility,
    Download,
    Upload,
    CheckCircle,
    Warning,
    Error as ErrorIcon,
    Schedule,
    AttachMoney,
    Business,
    Person,
    Phone,
    Email,
    MyLocation,
    GpsFixed,
    Map,
    PhotoCamera,
    Folder,
    InsertDriveFile,
    Share,
    MoreVert,
    ArrowForward,
    PlayArrow,
    Pause,
    Stop,
    Timeline,
    Assessment,
    BuildCircle,
    Engineering,
    Home,
    ExpandMore,
    ExpandLess,
    Send,
    Message,
    Star,
    StarBorder,
    AccessTime,
    CalendarToday,
    Group,
    Construction
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import projectManagerApi from '../../services/projectManagerApi';
import photoUploadService from '../../services/photoUploadService';
import messagingService from '../../services/messagingService';
import PMAvailableProjects from '../projectManager/PMAvailableProjects';
import PMPaymentRequests from '../projectManager/PMPaymentRequests';
import PMNotifications from '../projectManager/PMNotifications';
import DialogCloseButton from '../common/DialogCloseButton';

// Tab panel component
const TabPanel = ({ children, value, index, ...other }) => (
    <div
        role="tabpanel"
        hidden={value !== index}
        id={`pm-tabpanel-${index}`}
        {...other}
    >
        {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
);

const deriveHealth = (p) => {
    const pct = p.completion_percentage || 0;
    if (p.status === 'delayed' || p.status === 'on_hold') return 45;
    if (pct >= 80) return 90;
    if (pct >= 40) return 75;
    return 60;
};

const ProjectManagerDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedProject, setSelectedProject] = useState(null);
    const [geofenceDialog, setGeofenceDialog] = useState(false);
    const [documentDialog, setDocumentDialog] = useState(false);
    const [contractorSearchDialog, setContractorSearchDialog] = useState(false);
    const [projectSearchDialog, setProjectSearchDialog] = useState(false);

    // Open (or create) a conversation with a participant and go to messaging
    const openConversationWith = async (participantId) => {
        if (!participantId) return;
        try {
            const conv = await messagingService.getOrCreateConversation(participantId);
            const convId = conv?.data?.id || conv?.id;
            navigate(convId ? `/messages?conversation=${convId}` : '/messages');
        } catch (e) {
            navigate('/messages');
        }
    };

    // Dashboard data state
    const [stats, setStats] = useState({
        activeProjects: 0,
        totalProjects: 0,
        onTrackProjects: 0,
        delayedProjects: 0,
        contractorsManaged: 0,
        clientsServed: 0,
        totalBudgetManaged: 0,
        pendingApprovals: 0,
        siteVisitsThisWeek: 0,
        documentsGenerated: 0
    });

    const [projects, setProjects] = useState([]);
    const [contractors, setContractors] = useState([]);
    const [assignProjectId, setAssignProjectId] = useState('');
    const [assigningContractorId, setAssigningContractorId] = useState(null);
    const [assignFeedback, setAssignFeedback] = useState(null); // { severity, message }
    const [milestonesByProject, setMilestonesByProject] = useState({});
    const [signingMilestoneId, setSigningMilestoneId] = useState(null);
    const [milestoneFeedback, setMilestoneFeedback] = useState(null); // { severity, message }
    const [recentActivities, setRecentActivities] = useState([]);
    const [upcomingTasks, setUpcomingTasks] = useState([]);
    const [geofenceLocations, setGeofenceLocations] = useState([]);
    const [profileCompletion, setProfileCompletion] = useState(null);
    const [checkinPhotos, setCheckinPhotos] = useState([]);
    const [uploadingPhotos, setUploadingPhotos] = useState(false);

    // Report dialog controlled state
    const [reportType, setReportType] = useState('progress');
    const [reportProjectId, setReportProjectId] = useState(null);
    const [reportNotes, setReportNotes] = useState('');
    const [reportSubmitting, setReportSubmitting] = useState(false);

    // Milestone sign-off loaders
    const loadMilestones = async (projectId) => {
        try {
            const res = await projectManagerApi.getProjectMilestones(projectId);
            setMilestonesByProject((prev) => ({ ...prev, [projectId]: res?.data || [] }));
        } catch (e) {
            setMilestonesByProject((prev) => ({ ...prev, [projectId]: [] }));
        }
    };

    const handleSignOff = async (milestoneId, projectId) => {
        setSigningMilestoneId(milestoneId);
        try {
            await projectManagerApi.signOffMilestone(milestoneId, 'Reviewed by PM');
            setMilestoneFeedback({ severity: 'success', message: 'Milestone signed off.' });
            await loadMilestones(projectId);
        } catch (e) {
            setMilestoneFeedback({ severity: 'error', message: e?.response?.data?.error || 'Sign-off failed.' });
        } finally {
            setSigningMilestoneId(null);
        }
    };

    // Load dashboard data
    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            setError('');
            
            // Load profile completion status
            try {
                const completionResponse = await projectManagerApi.getProfileCompleteness();
                if (completionResponse.success) {
                    setProfileCompletion(completionResponse.data);
                }
            } catch (err) {
                console.warn('Failed to load profile completion:', err);
            }

            // Load dashboard stats from API
            const dashboardResponse = await projectManagerApi.getDashboard();
            if (dashboardResponse.success) {
                const dashboardData = dashboardResponse.data;
                setStats({
                    activeProjects: dashboardData.activeProjects || 0,
                    totalProjects: dashboardData.totalProjects || 0,
                    onTrackProjects: dashboardData.onTrackProjects || 0,
                    delayedProjects: dashboardData.delayedProjects || 0,
                    contractorsManaged: dashboardData.contractorsManaged || 0,
                    clientsServed: dashboardData.clientsServed || 0,
                    totalBudgetManaged: dashboardData.totalBudgetManaged || 0,
                    pendingApprovals: dashboardData.pendingPaymentRequests || 0,
                    siteVisitsThisWeek: dashboardData.siteVisitsThisWeek || 0,
                    documentsGenerated: 0 // Not tracked yet
                });
            }

            // Load MY projects (assigned to this PM) using the correct endpoint
            const myProjectsResponse = await projectManagerApi.getMyProjects();
            if (myProjectsResponse.success && myProjectsResponse.data) {
                // Transform projects data
                const transformed = myProjectsResponse.data.map(p => ({
                    id: p.id,
                    name: p.title,
                    client: {
                        name: `${p.owner?.firstName || ''} ${p.owner?.lastName || ''}`.trim() || 'Owner',
                        location: 'Diaspora',
                        avatar: null
                    },
                    contractor: { name: p.contractor_id ? 'Assigned' : 'Not Assigned', rating: 0 },
                    status: p.status || 'in_progress',
                    progress: p.completion_percentage || 0,
                    budget: parseFloat(p.budget || 0),
                    spent: parseFloat(p.spent || p.amount_spent || 0),
                    location: {
                        lat: 0,
                        lng: 0,
                        address: p.location || 'Location TBD'
                    },
                    startDate: p.created_at,
                    targetDate: null,
                    currentMilestone: (p.completion_percentage >= 100 ? 'Completed' : 'In Progress'),
                    nextMilestone: 'Foundation',
                    healthScore: deriveHealth(p),
                    lastSiteVisit: null,
                    pendingIssues: 0,
                    authorityLevel: p.authoritySettings?.authority_level
                }));
                setProjects(transformed);

                // Load site visits for geofence locations
                if (transformed.length > 0) {
                    const siteVisitsPromises = transformed.slice(0, 3).map(async (p) => {
                        try {
                            const visitsResponse = await projectManagerApi.getSiteVisits(p.id);
                            if (visitsResponse.success && visitsResponse.data.length > 0) {
                                const lastVisit = visitsResponse.data[0];
                                return {
                                    id: lastVisit.id,
                                    name: `${p.name} Site`,
                                    lat: parseFloat(lastVisit.check_in_latitude || 0),
                                    lng: parseFloat(lastVisit.check_in_longitude || 0),
                                    radius: 100,
                                    active: lastVisit.status === 'active',
                                    lastCheckIn: lastVisit.check_in_time
                                };
                            }
                        } catch (err) {
                            console.error(`Error loading site visits for ${p.id}:`, err);
                        }
                        return null;
                    });
                    
                    const locations = await Promise.all(siteVisitsPromises);
                    setGeofenceLocations(locations.filter(loc => loc !== null));
                }
            }

            // Load assignable contractors from the marketplace
            try {
                const contractorsRes = await projectManagerApi.getAssignableContractors();
                const list = contractorsRes?.data || [];
                setContractors(Array.isArray(list) ? list : []);
            } catch (e) {
                setContractors([]);
            }

            // Load notifications as recent activities
            const notificationsResponse = await projectManagerApi.getNotifications(true);
            if (notificationsResponse.success && notificationsResponse.data.notifications) {
                const activities = notificationsResponse.data.notifications.slice(0, 5).map((notif, idx) => ({
                    id: idx + 1,
                    type: notif.type === 'payment_request' ? 'payment' : 
                          notif.type === 'site_visit' ? 'site_visit' : 
                          notif.type === 'payment_approved' ? 'payment' : 'milestone',
                    message: notif.message,
                    time: new Date(notif.created_at).toLocaleString(),
                    project: notif.project_id
                }));
                setRecentActivities(activities);
            }

            setUpcomingTasks([]); // Would need task management API

        } catch (error) {
            console.error('Error loading dashboard data:', error);
            setError(error.message || 'Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const handleAssignContractor = async (contractorId) => {
        if (!assignProjectId) {
            setAssignFeedback({ severity: 'warning', message: 'Select a project to assign to first.' });
            return;
        }
        setAssigningContractorId(contractorId);
        try {
            const res = await projectManagerApi.assignContractor(assignProjectId, contractorId);
            const mode = res?.data?.mode;
            setAssignFeedback({
                severity: 'success',
                message: mode === 'assigned'
                    ? 'Contractor assigned to project.'
                    : 'Recommendation sent to the owner.'
            });
            await loadDashboardData();
        } catch (e) {
            setAssignFeedback({
                severity: 'error',
                message: e?.response?.data?.error || 'Assignment failed.'
            });
        } finally {
            setAssigningContractorId(null);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getStatusColor = (status) => {
        const colors = {
            'in_progress': 'primary',
            'on_hold': 'warning',
            'completed': 'success',
            'delayed': 'error',
            'planning': 'info'
        };
        return colors[status] || 'default';
    };

    const getHealthColor = (score) => {
        if (score >= 80) return 'success';
        if (score >= 60) return 'warning';
        return 'error';
    };

    const getPriorityColor = (priority) => {
        const colors = {
            'high': 'error',
            'medium': 'warning',
            'low': 'info'
        };
        return colors[priority] || 'default';
    };

    // Render Overview Tab
    const OverviewTab = () => (
        <Box>
            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)', color: 'white' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography variant="h3" fontWeight="bold">{stats.activeProjects}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Active Projects</Typography>
                                </Box>
                                <Assignment sx={{ fontSize: 40, opacity: 0.8 }} />
                            </Box>
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip label={`${stats.onTrackProjects} on track`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                                {stats.delayedProjects > 0 && (
                                    <Chip label={`${stats.delayedProjects} delayed`} size="small" sx={{ bgcolor: 'rgba(255,0,0,0.3)', color: 'white' }} />
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography variant="h3" fontWeight="bold">{formatCurrency(stats.totalBudgetManaged)}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Budget Under Management</Typography>
                                </Box>
                                <AttachMoney sx={{ fontSize: 40, opacity: 0.8 }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography variant="h3" fontWeight="bold">{stats.contractorsManaged}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Contractors Managed</Typography>
                                </Box>
                                <Engineering sx={{ fontSize: 40, opacity: 0.8 }} />
                            </Box>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    {stats.clientsServed} clients served
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <Box>
                                    <Typography variant="h3" fontWeight="bold">{stats.siteVisitsThisWeek}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Site Visits This Week</Typography>
                                </Box>
                                <MyLocation sx={{ fontSize: 40, opacity: 0.8 }} />
                            </Box>
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Badge badgeContent={stats.pendingApprovals} color="error">
                                    <Chip label="Pending approvals" size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
                                </Badge>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Profile Completion Alert */}
            {profileCompletion && !profileCompletion.isComplete && (
                <Alert 
                    severity={profileCompletion.completionPercentage >= 80 ? "warning" : "error"} 
                    sx={{ mb: 3 }}
                    icon={<Warning />}
                    action={
                        <Button 
                            color="inherit" 
                            size="small" 
                            onClick={() => navigate('/project-manager/profile')}
                        >
                            Complete Profile
                        </Button>
                    }
                >
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                        Profile Incomplete - {profileCompletion.completionPercentage}% Complete
                    </Typography>
                    <LinearProgress 
                        variant="determinate" 
                        value={profileCompletion.completionPercentage} 
                        sx={{ mb: 1, height: 8, borderRadius: 1 }}
                    />
                    <Typography variant="body2" component="div">
                        <strong>Required for full access:</strong> Please complete the following to access all features:
                        <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                            {profileCompletion.missingItems && profileCompletion.missingItems.map((item, idx) => (
                                <li key={idx}>
                                    <Typography variant="body2">{item}</Typography>
                                </li>
                            ))}
                        </Box>
                    </Typography>
                </Alert>
            )}

            {/* Quick Actions */}
            <Paper sx={{ p: 3, mb: 4 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>Quick Actions</Typography>
                <Grid container spacing={2}>
                    <Grid item xs={6} sm={4} md={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<Search />}
                            onClick={() => setProjectSearchDialog(true)}
                            sx={{ py: 1.5, flexDirection: 'column', gap: 1 }}
                        >
                            <Typography variant="caption">Find Projects</Typography>
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="secondary"
                            startIcon={<People />}
                            onClick={() => setContractorSearchDialog(true)}
                            sx={{ py: 1.5, flexDirection: 'column', gap: 1 }}
                        >
                            <Typography variant="caption">Find Contractors</Typography>
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="success"
                            startIcon={<MyLocation />}
                            onClick={() => setGeofenceDialog(true)}
                            sx={{ py: 1.5, flexDirection: 'column', gap: 1 }}
                        >
                            <Typography variant="caption">Site Check-In</Typography>
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="info"
                            startIcon={<Description />}
                            onClick={() => setDocumentDialog(true)}
                            sx={{ py: 1.5, flexDirection: 'column', gap: 1 }}
                        >
                            <Typography variant="caption">Generate Report</Typography>
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Button
                            fullWidth
                            variant="contained"
                            color="warning"
                            startIcon={<PhotoCamera />}
                            onClick={() => navigate('/pm/site-photos')}
                            sx={{ py: 1.5, flexDirection: 'column', gap: 1 }}
                        >
                            <Typography variant="caption">Upload Photos</Typography>
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={4} md={2}>
                        <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<Send />}
                            onClick={() => navigate('/messages')}
                            sx={{ py: 1.5, flexDirection: 'column', gap: 1 }}
                        >
                            <Typography variant="caption">Messages</Typography>
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Grid container spacing={3}>
                {/* Active Projects List */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" fontWeight={600}>My Managed Projects</Typography>
                            <Button size="small" onClick={() => setActiveTab(1)}>View All</Button>
                        </Box>
                        
                        {projects.slice(0, 3).map((project) => (
                            <Card key={project.id} sx={{ mb: 2, cursor: 'pointer', '&:hover': { boxShadow: 4 } }} onClick={() => setSelectedProject(project)}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={600}>{project.name}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Client: {project.client.name} ({project.client.location})
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Chip 
                                                label={project.status.replace('_', ' ')} 
                                                color={getStatusColor(project.status)} 
                                                size="small" 
                                            />
                                            <Tooltip title={`Health Score: ${project.healthScore}%`}>
                                                <Chip 
                                                    icon={project.healthScore >= 80 ? <CheckCircle /> : project.healthScore >= 60 ? <Warning /> : <ErrorIcon />}
                                                    label={`${project.healthScore}%`}
                                                    color={getHealthColor(project.healthScore)}
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    <Box sx={{ mb: 2 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="caption">Progress</Typography>
                                            <Typography variant="caption">{project.progress}%</Typography>
                                        </Box>
                                        <LinearProgress 
                                            variant="determinate" 
                                            value={project.progress} 
                                            sx={{ height: 8, borderRadius: 4 }}
                                        />
                                    </Box>

                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Current Milestone</Typography>
                                            <Typography variant="body2" fontWeight={500}>{project.currentMilestone}</Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="caption" color="text.secondary">Budget</Typography>
                                            <Typography variant="body2" fontWeight={500}>
                                                {formatCurrency(project.spent)} / {formatCurrency(project.budget)}
                                            </Typography>
                                        </Grid>
                                    </Grid>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <LocationOn fontSize="small" color="action" />
                                            <Typography variant="caption" color="text.secondary">{project.location.address}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <Tooltip title="View Details">
                                                <IconButton size="small"><Visibility /></IconButton>
                                            </Tooltip>
                                            <Tooltip title="Site Check-In">
                                                <IconButton size="small" color="success"><MyLocation /></IconButton>
                                            </Tooltip>
                                            <Tooltip title="Generate Report">
                                                <IconButton size="small" color="primary"><Description /></IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                    </Paper>
                </Grid>

                {/* Sidebar */}
                <Grid item xs={12} md={4}>
                    {/* Upcoming Tasks */}
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom fontWeight={600}>Upcoming Tasks</Typography>
                        <List dense>
                            {upcomingTasks.map((task) => (
                                <ListItem key={task.id} sx={{ px: 0 }}>
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: `${getPriorityColor(task.priority)}.light`, width: 32, height: 32 }}>
                                            <Schedule fontSize="small" />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={task.task}
                                        secondary={task.date}
                                        primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                    <Chip label={task.priority} size="small" color={getPriorityColor(task.priority)} />
                                </ListItem>
                            ))}
                        </List>
                        <Button fullWidth variant="outlined" size="small" sx={{ mt: 1 }}>View All Tasks</Button>
                    </Paper>

                    {/* Recent Activity */}
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom fontWeight={600}>Recent Activity</Typography>
                        <List dense>
                            {recentActivities.slice(0, 5).map((activity) => (
                                <ListItem key={activity.id} sx={{ px: 0 }}>
                                    <ListItemAvatar>
                                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                                            {activity.type === 'milestone' && <CheckCircle fontSize="small" />}
                                            {activity.type === 'site_visit' && <MyLocation fontSize="small" />}
                                            {activity.type === 'document' && <Description fontSize="small" />}
                                            {activity.type === 'payment' && <AttachMoney fontSize="small" />}
                                            {activity.type === 'issue' && <Warning fontSize="small" />}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={activity.message}
                                        secondary={activity.time}
                                        primaryTypographyProps={{ variant: 'body2' }}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );

    // Render Projects Tab
    const ProjectsTab = () => {
        const [myProjects, setMyProjects] = useState([]);
        const [loadingProjects, setLoadingProjects] = useState(true);

        useEffect(() => {
            const loadMyProjects = async () => {
                try {
                    setLoadingProjects(true);
                    const response = await projectManagerApi.getMyProjects();
                    if (response.success) {
                        // Transform to match the expected format
                        const transformed = response.data.map(p => ({
                            id: p.id,
                            name: p.title,
                            ownerId: p.owner?.id,
                            contractorId: p.contractor_id,
                            client: {
                                name: `${p.owner?.firstName || ''} ${p.owner?.lastName || ''}`.trim() || 'Owner',
                                location: 'Diaspora',
                                avatar: null
                            },
                            contractor: { name: p.contractor_id ? 'Assigned' : 'Not Assigned', rating: 0 },
                            status: p.status || 'in_progress',
                            progress: p.completion_percentage || 0,
                            budget: parseFloat(p.budget || 0),
                            spent: parseFloat(p.spent || p.amount_spent || 0),
                            location: {
                                lat: 0,
                                lng: 0,
                                address: p.location || 'Location TBD'
                            },
                            startDate: p.created_at,
                            targetDate: null,
                            currentMilestone: (p.completion_percentage >= 100 ? 'Completed' : 'In Progress'),
                            nextMilestone: 'Foundation',
                            healthScore: deriveHealth(p),
                            lastSiteVisit: null,
                            pendingIssues: 0,
                            authorityLevel: p.authoritySettings?.authority_level
                        }));
                        setMyProjects(transformed);
                    }
                } catch (err) {
                    console.error('Error loading my projects:', err);
                } finally {
                    setLoadingProjects(false);
                }
            };
            loadMyProjects();
        }, []);

        const filteredProjects = myProjects.filter(p => 
            !searchQuery || 
            p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            p.client.name.toLowerCase().includes(searchQuery.toLowerCase())
        );

        if (loadingProjects) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        return (
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" fontWeight={600}>My Managed Projects</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Search projects..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
                            }}
                        />
                    </Box>
                </Box>

                {filteredProjects.length === 0 ? (
                    <Alert severity="info">
                        {myProjects.length === 0 
                            ? "You don't have any assigned projects yet. Browse 'Available Projects' to apply."
                            : "No projects match your search."}
                    </Alert>
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'grey.100' }}>
                                    <TableCell>Project Name</TableCell>
                                    <TableCell>Client</TableCell>
                                    <TableCell>Contractor</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Progress</TableCell>
                                    <TableCell>Budget</TableCell>
                                    <TableCell>Authority</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredProjects.map((project) => (
                                    <React.Fragment key={project.id}>
                                    <TableRow hover>
                                <TableCell>
                                    <Box>
                                        <Typography variant="body2" fontWeight={500}>{project.name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{project.location.address}</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Avatar sx={{ width: 24, height: 24 }}>{project.client.name[0]}</Avatar>
                                        <Box>
                                            <Typography variant="body2">{project.client.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{project.client.location}</Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{project.contractor.name}</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Star sx={{ fontSize: 14, color: 'warning.main' }} />
                                                <Typography variant="caption">{project.contractor.rating}</Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={project.status.replace('_', ' ')} color={getStatusColor(project.status)} size="small" />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ width: 100 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                    <Typography variant="caption">{project.progress}%</Typography>
                                                </Box>
                                                <LinearProgress variant="determinate" value={project.progress} sx={{ height: 6, borderRadius: 3 }} />
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{formatCurrency(project.budget)}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={project.authorityLevel || 'N/A'}
                                                color="primary"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                <Tooltip title="View Details">
                                                    <IconButton size="small" onClick={() => navigate(`/projects/${project.id}`)}>
                                                        <Visibility />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Site Check-In">
                                                    <IconButton 
                                                        size="small" 
                                                        color="success"
                                                        onClick={() => {
                                                            setSelectedProject(project);
                                                            setGeofenceDialog(true);
                                                        }}
                                                    >
                                                        <MyLocation />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Payment Requests">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => {
                                                            setActiveTab(3); // Switch to Payment Requests tab
                                                        }}
                                                    >
                                                        <AttachMoney />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Message Owner">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openConversationWith(project.ownerId)}
                                                            disabled={!project.ownerId}
                                                        >
                                                            <Message />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Message Contractor">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            color="secondary"
                                                            onClick={() => openConversationWith(project.contractorId)}
                                                            disabled={!project.contractorId}
                                                        >
                                                            <Message />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                            <Button
                                                size="small"
                                                sx={{ mt: 0.5 }}
                                                onClick={() => {
                                                    if (milestonesByProject[project.id]) {
                                                        setMilestonesByProject((prev) => {
                                                            const next = { ...prev };
                                                            delete next[project.id];
                                                            return next;
                                                        });
                                                    } else {
                                                        loadMilestones(project.id);
                                                    }
                                                }}
                                            >
                                                {milestonesByProject[project.id] ? 'Hide milestones' : 'View milestones'}
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                    {milestonesByProject[project.id] && (
                                        <TableRow>
                                            <TableCell colSpan={8} sx={{ py: 1, bgcolor: 'grey.50' }}>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Escrow Milestones</Typography>
                                                {milestoneFeedback && (
                                                    <Alert
                                                        severity={milestoneFeedback.severity}
                                                        sx={{ mb: 1 }}
                                                        onClose={() => setMilestoneFeedback(null)}
                                                    >
                                                        {milestoneFeedback.message}
                                                    </Alert>
                                                )}
                                                {(milestonesByProject[project.id] || []).length === 0 ? (
                                                    <Typography variant="body2" color="text.secondary">
                                                        No milestones found for this project.
                                                    </Typography>
                                                ) : (
                                                    (milestonesByProject[project.id] || []).map((m) => (
                                                        <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                                            <Typography variant="body2" sx={{ flex: 1 }}>
                                                                #{m.milestone_number} · {m.description} · {m.status}
                                                                {m.pm_approved ? ' · ✅ signed off' : ''}
                                                            </Typography>
                                                            <Button
                                                                size="small"
                                                                disabled={m.pm_approved || signingMilestoneId === m.id}
                                                                onClick={() => handleSignOff(m.id, project.id)}
                                                            >
                                                                {m.pm_approved ? 'Signed off' : (signingMilestoneId === m.id ? 'Signing…' : 'Sign off')}
                                                            </Button>
                                                        </Box>
                                                    ))
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        );
    };

    // Render Contractors Tab
    const ContractorsTab = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>Contractors Network</Typography>
                <Button variant="contained" startIcon={<Search />} onClick={() => setContractorSearchDialog(true)}>
                    Find New Contractors
                </Button>
            </Box>

            <FormControl size="small" sx={{ minWidth: 240, mb: 2 }}>
                <InputLabel>Assign to project</InputLabel>
                <Select
                    label="Assign to project"
                    value={assignProjectId}
                    onChange={(e) => setAssignProjectId(e.target.value)}
                >
                    {projects.map((p) => (
                        <MenuItem key={p.id} value={p.id}>{p.name || p.title || p.id}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {assignFeedback && (
                <Alert severity={assignFeedback.severity} sx={{ mb: 2 }} onClose={() => setAssignFeedback(null)}>
                    {assignFeedback.message}
                </Alert>
            )}

            <Grid container spacing={3}>
                {contractors.map((contractor) => (
                    <Grid item xs={12} md={4} key={contractor.id}>
                        <Card>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                    <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                                        {contractor.name[0]}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="subtitle1" fontWeight={600}>{contractor.name}</Typography>
                                            {contractor.verified && (
                                                <Tooltip title="Verified Contractor">
                                                    <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                                                </Tooltip>
                                            )}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">{contractor.specialization}</Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                                    <Star sx={{ color: 'warning.main' }} />
                                    <Typography variant="h6" fontWeight={600}>{contractor.rating}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        ({contractor.completedProjects} projects)
                                    </Typography>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Active Projects</Typography>
                                        <Typography variant="h6">{contractor.activeProjects}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="caption" color="text.secondary">Completed</Typography>
                                        <Typography variant="h6">{contractor.completedProjects}</Typography>
                                    </Grid>
                                </Grid>

                                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        startIcon={<Visibility />}
                                        onClick={() => navigate(`/contractors/${contractor.id}`)}
                                    >
                                        View Profile
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        disabled={assigningContractorId === contractor.id}
                                        onClick={() => handleAssignContractor(contractor.id)}
                                    >
                                        {assigningContractorId === contractor.id ? 'Assigning…' : 'Assign'}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );

    // Render Geofencing Tab
    const GeofencingTab = () => {
        const [siteVisitHistory, setSiteVisitHistory] = useState([]);
        const [loadingVisits, setLoadingVisits] = useState(true);

        useEffect(() => {
            const projectId = selectedProject?.id || projects[0]?.id;
            if (!projectId) {
                setLoadingVisits(false);
                return;
            }
            const load = async () => {
                try {
                    setLoadingVisits(true);
                    const res = await projectManagerApi.getSiteVisits(projectId);
                    if (res.success) {
                        setSiteVisitHistory(res.data || []);
                    }
                } catch (err) {
                    console.error('Error loading site visit history:', err);
                } finally {
                    setLoadingVisits(false);
                }
            };
            load();
        }, []);

        return (
        <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
                <AlertTitle>Geofencing & Site Check-In</AlertTitle>
                Track your site visits with GPS verification. Check in when you arrive at a construction site to log your presence and time spent.
            </Alert>

            <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Map sx={{ fontSize: 80, color: 'grey.400', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">Interactive Map View</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Map integration showing all your project sites with geofence boundaries
                            </Typography>
                            <Button variant="contained" sx={{ mt: 2 }} startIcon={<MyLocation />}>
                                Enable Location Services
                            </Button>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom fontWeight={600}>Site Locations</Typography>
                        
                        {geofenceLocations.map((location) => (
                            <Card key={location.id} sx={{ mb: 2 }}>
                                <CardContent sx={{ pb: '16px !important' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Box>
                                            <Typography variant="subtitle2">{location.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                Radius: {location.radius}m
                                            </Typography>
                                        </Box>
                                        <FormControlLabel
                                            control={<Switch checked={location.active} size="small" />}
                                            label=""
                                        />
                                    </Box>
                                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AccessTime fontSize="small" color="action" />
                                        <Typography variant="caption" color="text.secondary">
                                            Last check-in: {location.lastCheckIn}
                                        </Typography>
                                    </Box>
                                    <Button 
                                        fullWidth 
                                        variant="contained" 
                                        size="small" 
                                        color={location.active ? 'success' : 'inherit'}
                                        startIcon={<MyLocation />}
                                        sx={{ mt: 2 }}
                                        disabled={!location.active}
                                    >
                                        Check In Now
                                    </Button>
                                </CardContent>
                            </Card>
                        ))}
                    </Paper>
                </Grid>
            </Grid>

            {/* Check-in History */}
            <Paper sx={{ p: 3, mt: 3 }}>
                <Typography variant="h6" gutterBottom fontWeight={600}>Recent Check-ins</Typography>
                {loadingVisits ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Visit Type</TableCell>
                                    <TableCell>Check-in Time</TableCell>
                                    <TableCell>Check-out Time</TableCell>
                                    <TableCell>Duration</TableCell>
                                    <TableCell>GPS Verified</TableCell>
                                    <TableCell>Notes</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {siteVisitHistory.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                            No site visits yet
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    siteVisitHistory.map((visit) => {
                                        const durationLabel = visit.duration_minutes
                                            ? `${Math.floor(visit.duration_minutes / 60)}h ${visit.duration_minutes % 60}m`
                                            : '—';
                                        return (
                                            <TableRow key={visit.id}>
                                                <TableCell>{visit.visit_type || '—'}</TableCell>
                                                <TableCell>{visit.check_in_time ? new Date(visit.check_in_time).toLocaleString() : '—'}</TableCell>
                                                <TableCell>{visit.check_out_time ? new Date(visit.check_out_time).toLocaleString() : '—'}</TableCell>
                                                <TableCell>{durationLabel}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={visit.gps_verified ? 'Verified' : 'Unverified'}
                                                        color={visit.gps_verified ? 'success' : 'default'}
                                                        size="small"
                                                    />
                                                </TableCell>
                                                <TableCell>{visit.notes || '—'}</TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
        );
    };

    // Render Documents Tab
    const DocumentsTab = () => {
        const [reports, setReports] = useState([]);
        const [loadingReports, setLoadingReports] = useState(true);

        useEffect(() => {
            const projectId = selectedProject?.id || projects[0]?.id;
            if (!projectId) {
                setLoadingReports(false);
                return;
            }
            const load = async () => {
                try {
                    setLoadingReports(true);
                    const res = await projectManagerApi.getReports(projectId);
                    if (res.success) {
                        setReports(res.data || []);
                    }
                } catch (err) {
                    console.error('Error loading reports:', err);
                } finally {
                    setLoadingReports(false);
                }
            };
            load();
        }, []);

        return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>Documents & Reports</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button variant="outlined" startIcon={<Upload />}>Upload Document</Button>
                    <Button variant="contained" startIcon={<Add />} onClick={() => setDocumentDialog(true)}>
                        Generate Report
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Report Templates */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight={600}>Report Templates</Typography>
                        <List>
                            {[
                                { name: 'Weekly Progress Report', icon: <Timeline /> },
                                { name: 'Monthly Summary', icon: <Assessment /> },
                                { name: 'Site Inspection Report', icon: <Search /> },
                                { name: 'Budget Analysis', icon: <AttachMoney /> },
                                { name: 'Milestone Completion', icon: <CheckCircle /> },
                                { name: 'Client Update Brief', icon: <Person /> }
                            ].map((template, index) => (
                                <ListItem
                                    key={index}
                                    button
                                    sx={{ borderRadius: 1, mb: 1 }}
                                    onClick={() => setDocumentDialog(true)}
                                >
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: 'primary.light' }}>{template.icon}</Avatar>
                                    </ListItemAvatar>
                                    <ListItemText primary={template.name} />
                                    <ArrowForward fontSize="small" color="action" />
                                </ListItem>
                            ))}
                        </List>
                    </Paper>
                </Grid>

                {/* Recent Documents */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight={600}>Recent Documents</Typography>
                        {loadingReports ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Document</TableCell>
                                            <TableCell>Type</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reports.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                                    No reports generated yet
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            reports.map((r) => (
                                                <TableRow key={r.id}>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <InsertDriveFile color="primary" />
                                                            <Typography variant="body2">{r.report_type} Report</Typography>
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell><Chip label={r.report_type} size="small" /></TableCell>
                                                    <TableCell>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Tooltip title="View PDF">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => window.open(projectManagerApi.getReportPdfUrl(r.id), '_blank')}
                                                                >
                                                                    <Visibility />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
        );
    };

    // Render Available Projects Tab
    const AvailableProjectsTab = () => (
        <Box>
            <PMAvailableProjects 
                onApplicationSubmitted={() => {
                    loadDashboardData();
                    setActiveTab(5); // Switch to notifications tab
                }}
            />
        </Box>
    );

    // Render Payment Requests Tab
    const PaymentRequestsTab = () => {
        const [selectedProjectId, setSelectedProjectId] = useState(null);

        if (projects.length === 0) {
            return (
                <Alert severity="info">
                    You need to be assigned to a project first to create payment requests.
                </Alert>
            );
        }

        return (
            <Box>
                {projects.length > 1 && (
                    <FormControl fullWidth sx={{ mb: 3 }}>
                        <InputLabel>Select Project</InputLabel>
                        <Select
                            value={selectedProjectId || projects[0]?.id || ''}
                            label="Select Project"
                            onChange={(e) => setSelectedProjectId(e.target.value)}
                        >
                            {projects.map(p => (
                                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                <PMPaymentRequests 
                    projectId={selectedProjectId || projects[0]?.id}
                    isOwner={false}
                />
            </Box>
        );
    };

    // Render Notifications Tab
    const NotificationsTab = () => (
        <Box>
            <PMNotifications 
                onNotificationClick={(url) => {
                    if (url) {
                        navigate(url);
                    }
                }}
            />
        </Box>
    );

    // Render Analytics Tab
    const AnalyticsTab = () => (
        <Box>
            <Typography variant="h6" gutterBottom fontWeight={600}>Performance Analytics</Typography>
            
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <TrendingUp sx={{ fontSize: 60, color: 'grey.400' }} />
                            <Typography variant="h6" color="text.secondary">Project Completion Trends</Typography>
                            <Typography variant="body2" color="text.secondary">Chart visualization coming soon</Typography>
                        </Box>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Assessment sx={{ fontSize: 60, color: 'grey.400' }} />
                            <Typography variant="h6" color="text.secondary">Budget Performance</Typography>
                            <Typography variant="body2" color="text.secondary">Chart visualization coming soon</Typography>
                        </Box>
                    </Paper>
                </Grid>

                {/* Key Metrics */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight={600}>Key Performance Indicators</Typography>
                        <Grid container spacing={3}>
                            {[
                                { label: 'On-Time Delivery Rate', value: '--' },
                                { label: 'Budget Adherence', value: '--' },
                                { label: 'Client Satisfaction', value: '--' },
                                { label: 'Issue Resolution Time', value: '--' },
                                { label: 'Site Visits This Week', value: stats.siteVisitsThisWeek },
                                { label: 'Documents Generated', value: stats.documentsGenerated }
                            ].map((metric, index) => (
                                <Grid item xs={6} md={2} key={index}>
                                    <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                        <Typography variant="h5" fontWeight={600} color="primary">{metric.value}</Typography>
                                        <Typography variant="body2" color="text.secondary">{metric.label}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                        <Typography variant="h4" fontWeight={700} gutterBottom>
                            Welcome back, {user?.firstName}!
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Manage your construction projects and teams efficiently
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Badge badgeContent={stats.pendingApprovals} color="error">
                            <IconButton>
                                <Notifications />
                            </IconButton>
                        </Badge>
                        <Button variant="outlined" onClick={() => navigate('/profile')}>
                            My Profile
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs 
                    value={activeTab} 
                    onChange={(e, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                >
                    <Tab icon={<Dashboard />} label="Overview" iconPosition="start" />
                    <Tab icon={<Assignment />} label="My Projects" iconPosition="start" />
                    <Tab icon={<Search />} label="Available Projects" iconPosition="start" />
                    <Tab icon={<AttachMoney />} label="Payment Requests" iconPosition="start" />
                    <Tab icon={<MyLocation />} label="Site Visits" iconPosition="start" />
                    <Tab icon={<Notifications />} label="Notifications" iconPosition="start" />
                    <Tab icon={<People />} label="Contractors" iconPosition="start" />
                    <Tab icon={<Description />} label="Documents" iconPosition="start" />
                    <Tab icon={<TrendingUp />} label="Analytics" iconPosition="start" />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            <TabPanel value={activeTab} index={0}><OverviewTab /></TabPanel>
            <TabPanel value={activeTab} index={1}><ProjectsTab /></TabPanel>
            <TabPanel value={activeTab} index={2}><AvailableProjectsTab /></TabPanel>
            <TabPanel value={activeTab} index={3}><PaymentRequestsTab /></TabPanel>
            <TabPanel value={activeTab} index={4}><GeofencingTab /></TabPanel>
            <TabPanel value={activeTab} index={5}><NotificationsTab /></TabPanel>
            <TabPanel value={activeTab} index={6}><ContractorsTab /></TabPanel>
            <TabPanel value={activeTab} index={7}><DocumentsTab /></TabPanel>
            <TabPanel value={activeTab} index={8}><AnalyticsTab /></TabPanel>

            {/* Geofence Check-In Dialog */}
            <Dialog open={geofenceDialog} onClose={() => setGeofenceDialog(false)} maxWidth="sm" fullWidth>
                <DialogCloseButton onClick={() => setGeofenceDialog(false)} />
                <DialogTitle>Site Check-In</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Your location will be verified using GPS to confirm you are at the project site.
                    </Alert>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Select Project Site</InputLabel>
                        <Select 
                            label="Select Project Site" 
                            value={selectedProject?.id || ''}
                            onChange={(e) => {
                                const proj = projects.find(p => p.id === e.target.value);
                                setSelectedProject(proj);
                            }}
                        >
                            {projects.map(proj => (
                                <MenuItem key={proj.id} value={proj.id}>{proj.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Check-In Notes"
                        placeholder="What are you inspecting today?"
                        sx={{ mb: 2 }}
                        id="checkin-notes"
                    />
                    <Box sx={{ mb: 2 }}>
                        <Button
                            component="label"
                            variant="outlined"
                            startIcon={uploadingPhotos ? <CircularProgress size={16} /> : <PhotoCamera />}
                            disabled={uploadingPhotos}
                        >
                            {uploadingPhotos ? 'Uploading…' : 'Attach Photos'}
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                hidden
                                onChange={async (e) => {
                                    const files = Array.from(e.target.files || []);
                                    if (!files.length) return;
                                    setUploadingPhotos(true);
                                    try {
                                        const urls = [];
                                        for (const f of files) {
                                            const res = await photoUploadService.uploadPhoto(f);
                                            urls.push(res.photoUrl);
                                        }
                                        setCheckinPhotos(prev => [...prev, ...urls]);
                                    } catch (err) {
                                        alert('Photo upload failed: ' + (err.message || 'Unknown error'));
                                    } finally {
                                        setUploadingPhotos(false);
                                        e.target.value = '';
                                    }
                                }}
                            />
                        </Button>
                        {checkinPhotos.length > 0 && (
                            <Typography variant="caption" sx={{ ml: 1, color: 'success.main' }}>
                                ({checkinPhotos.length}) photo{checkinPhotos.length !== 1 ? 's' : ''} attached
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                        <GpsFixed sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                        <Typography variant="body2">Click below to capture your GPS location</Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGeofenceDialog(false)}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        startIcon={<MyLocation />} 
                        color="success"
                        onClick={async () => {
                            if (!selectedProject) {
                                alert('Please select a project');
                                return;
                            }
                            try {
                                // Get GPS coordinates
                                navigator.geolocation.getCurrentPosition(
                                    async (position) => {
                                        const notes = document.getElementById('checkin-notes')?.value || '';
                                        const response = await projectManagerApi.checkIn({
                                            projectId: selectedProject.id,
                                            latitude: position.coords.latitude,
                                            longitude: position.coords.longitude,
                                            visitType: 'inspection',
                                            notes,
                                            photos: checkinPhotos
                                        });
                                        if (response.success) {
                                            setGeofenceDialog(false);
                                            setCheckinPhotos([]);
                                            loadDashboardData();
                                        }
                                    },
                                    (error) => {
                                        alert('GPS access denied. Please enable location services.');
                                    }
                                );
                            } catch (err) {
                                console.error('Check-in error:', err);
                                alert('Failed to check in. Please try again.');
                            }
                        }}
                    >
                        Check In Now
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Generate Report Dialog */}
            <Dialog open={documentDialog} onClose={() => setDocumentDialog(false)} maxWidth="sm" fullWidth>
                <DialogCloseButton onClick={() => setDocumentDialog(false)} />
                <DialogTitle>Generate Report</DialogTitle>
                <DialogContent>
                    <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                        <InputLabel>Report Type</InputLabel>
                        <Select
                            label="Report Type"
                            value={reportType}
                            onChange={(e) => setReportType(e.target.value)}
                        >
                            <MenuItem value="progress">Weekly Progress Report</MenuItem>
                            <MenuItem value="monthly">Monthly Summary</MenuItem>
                            <MenuItem value="inspection">Site Inspection Report</MenuItem>
                            <MenuItem value="budget">Budget Analysis</MenuItem>
                            <MenuItem value="milestone">Milestone Completion Report</MenuItem>
                            <MenuItem value="client_update">Client Update Brief</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Project</InputLabel>
                        <Select
                            label="Project"
                            value={reportProjectId || ''}
                            onChange={(e) => setReportProjectId(e.target.value || null)}
                        >
                            <MenuItem value="">Select a project…</MenuItem>
                            {projects.map(p => (
                                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Additional Notes"
                        placeholder="Any specific items to highlight in the report..."
                        sx={{ mb: 2 }}
                        value={reportNotes}
                        onChange={(e) => setReportNotes(e.target.value)}
                    />
                    <FormControlLabel
                        control={<Switch defaultChecked />}
                        label="Include photos from site visits"
                    />
                    <FormControlLabel
                        control={<Switch defaultChecked />}
                        label="Send copy to client"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDocumentDialog(false)} disabled={reportSubmitting}>Cancel</Button>
                    <Button
                        variant="contained"
                        startIcon={reportSubmitting ? <CircularProgress size={16} color="inherit" /> : <Description />}
                        disabled={reportSubmitting}
                        onClick={async () => {
                            if (!reportProjectId) {
                                alert('Select a project');
                                return;
                            }
                            setReportSubmitting(true);
                            try {
                                const res = await projectManagerApi.createReport({
                                    projectId: reportProjectId,
                                    reportType,
                                    narrative: reportNotes
                                });
                                if (res.success) {
                                    setDocumentDialog(false);
                                    setReportNotes('');
                                    loadDashboardData();
                                    setActiveTab(7); // Documents tab
                                }
                            } catch (err) {
                                alert('Failed to generate report: ' + (err.message || 'Unknown error'));
                            } finally {
                                setReportSubmitting(false);
                            }
                        }}
                    >
                        {reportSubmitting ? 'Generating…' : 'Generate Report'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Contractor Search Dialog */}
            <Dialog open={contractorSearchDialog} onClose={() => setContractorSearchDialog(false)} maxWidth="md" fullWidth>
                <DialogCloseButton onClick={() => setContractorSearchDialog(false)} />
                <DialogTitle>Find Contractors</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="Search by name or specialization"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Location</InputLabel>
                                <Select label="Location" defaultValue="">
                                    <MenuItem value="">All Locations</MenuItem>
                                    <MenuItem value="harare">Harare</MenuItem>
                                    <MenuItem value="bulawayo">Bulawayo</MenuItem>
                                    <MenuItem value="mutare">Mutare</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Specialization</InputLabel>
                                <Select label="Specialization" defaultValue="">
                                    <MenuItem value="">All</MenuItem>
                                    <MenuItem value="residential">Residential</MenuItem>
                                    <MenuItem value="commercial">Commercial</MenuItem>
                                    <MenuItem value="renovation">Renovation</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    
                    <Divider sx={{ my: 3 }} />
                    
                    <Typography variant="subtitle2" gutterBottom>Search Results</Typography>
                    <List>
                        {contractors.map(contractor => (
                            <ListItem key={contractor.id} divider>
                                <ListItemAvatar>
                                    <Avatar>{contractor.name[0]}</Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={contractor.name}
                                    secondary={`${contractor.specialization} • ${contractor.completedProjects} projects completed`}
                                />
                                <ListItemSecondaryAction>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Star sx={{ color: 'warning.main', fontSize: 18 }} />
                                            <Typography variant="body2" sx={{ ml: 0.5 }}>{contractor.rating}</Typography>
                                        </Box>
                                        <Button size="small" variant="outlined">View Profile</Button>
                                        <Button size="small" variant="contained">Contact</Button>
                                    </Box>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setContractorSearchDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Project Search Dialog */}
            <Dialog open={projectSearchDialog} onClose={() => setProjectSearchDialog(false)} maxWidth="md" fullWidth>
                <DialogCloseButton onClick={() => setProjectSearchDialog(false)} />
                <DialogTitle>Find Projects</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="Search projects..."
                                InputProps={{
                                    startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select label="Status" defaultValue="">
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="planning">Planning</MenuItem>
                                    <MenuItem value="in_progress">In Progress</MenuItem>
                                    <MenuItem value="on_hold">On Hold</MenuItem>
                                    <MenuItem value="completed">Completed</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Alert severity="info" sx={{ mt: 2 }}>
                        Browse available projects from property owners looking for project managers.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setProjectSearchDialog(false)}>Close</Button>
                    <Button variant="contained" onClick={() => navigate('/marketplace')}>
                        Go to Marketplace
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ProjectManagerDashboard;





